import react from 'react';
import { LineChart, LineChartSeries } from '@components/chart/lineChart';
import * as am4charts from '@amcharts/amcharts4/charts';
import moment from 'moment-timezone';
import { TTypedTFunction, useTranslation } from '@lib/useTypedTranslation';
import { drawXAxisLabels, drawYAxisLabelsLineChart } from '../../../containers/battery-centric/compositions/lib';
import { IBatteryLevel, ITzBatteryLevel } from '../../../services/core/devicePerformance';
import { groupBy, isEmpty } from 'lodash';
import { formatDate } from '../../core/device/compositions/performance/charts/deviceChargeLevel';
import './styles.css';

interface ICommonDeviceLevelChartProps {
  date: number,
  isLoading?: boolean,
  title?: string
}

// `displayTimeZone` selects the data shape at the type boundary: zoned callers must supply records
// carrying a real `utc` instant (and `date` is a real UTC instant within the selected local day),
// ordinary callers keep the device-local `local` shape.
export type IDeviceLevelChart = ICommonDeviceLevelChartProps & (
  { displayTimeZone?: undefined, data: IBatteryLevel[] } |
  { displayTimeZone: string, data: ITzBatteryLevel[] }
);

export interface IBatteryLevelData {
  local: string,
  level: number,
  smartBattery?: {
    serialNumber: string
  }
}

export interface ITzBatteryLevelData extends IBatteryLevelData {
  utc: string
}

export const DeviceLevelChart = (props: IDeviceLevelChart) => {
  const { t } = useTranslation(['translation', 'performance']);
  const title = t(`DEVICE_CHARGE_LEVEL_FOR`, { date: moment.utc(props.date).tz(props.displayTimeZone || 'UTC').format('Do MMMM'), ns: 'translation' });
  const { series, combinedBatteriesData } = getDeviceChargeLevelData(props?.data as IBatteryLevelData[], props.displayTimeZone);

  return (
    <div className="device-charge-level-chart-container">
      <LineChart<any>
        cypressId="deviceChargeLevelChart"
        data={combinedBatteriesData}
        series={series}
        title={title}
        className="device-charge-level"
        hasLegend={true}
        useAxisTooltip={true}
        drawXAxis={(_, xAxis: am4charts.DateAxis) => drawXAxisLabels(xAxis, props.date, props.displayTimeZone)}
        drawYAxis={(_, yAxis: am4charts.ValueAxis) => drawYAxisLabelsLineChart(yAxis)}
        adjustChartDimensions={{ length: 97.5, height: 240 }}
        hasNoSelectionOverlay={!props.isLoading}
        getSeriesToolTipAdapter={() => getDeviceChargeLevelTooltipAdapter(t, props.displayTimeZone)}
      />
    </div>
  );
};

/**
 * Ordinary callers provide device-local data and are positioned by `local`. Zoned callers (those
 * passing `displayTimeZone`) provide records with a real `utc` instant and are positioned by it,
 * so two repeated fall-back wall-clock hours stay two distinct points. The chart's date field is
 * named `local` either way.
 */
export const getDeviceChargeLevelData = (data: IBatteryLevelData[], displayTimeZone?: string) : { series: LineChartSeries<any>[], combinedBatteriesData:Array<{ local: number } & Record<string, number>> } => {
  const lineAndToolTipColour = ['#5ECAEA', '#709BDF', '#8076E0', '#3BB09A', '#DF7097', '#EA985E', '#5FB952'];
  if (isEmpty(data)) {
    return { series: [{
      visible: true,
      dataKey: 'level',
      description: '-',
      colour: lineAndToolTipColour[0]
    }],
    combinedBatteriesData: [] };
  }
  const dataGroupedByBattery = groupBy(data, (item) => item.smartBattery?.serialNumber ? item.smartBattery.serialNumber : '-');
  const sortedBatteries = Object.keys(dataGroupedByBattery);
  const combinedBatteriesData: Array<{ local: number } & Record<string, number>> = [];

  const localMap = new Map<number, { local: number } & Record<string, number>>();

  data.forEach(item => {
    const { level, smartBattery } = item;
    const serial = smartBattery?.serialNumber ? smartBattery.serialNumber : '-';
    const timestamp = displayTimeZone ? Number((item as ITzBatteryLevelData).utc) : parseInt(item.local);
    if (!localMap.has(timestamp)) {
      localMap.set(timestamp, { local: timestamp });
    }

    localMap.get(timestamp)[serial] = level;
  });

  localMap.forEach(value => combinedBatteriesData.push(value));
  combinedBatteriesData.sort((a, b) => a.local - b.local);

  const series: LineChartSeries<any>[] = sortedBatteries.map((batterySerialNumber: string, index: number) => ({
    visible: true,
    dataKey: batterySerialNumber,
    description: batterySerialNumber,
    colour: lineAndToolTipColour[index]
  }));

  return { series, combinedBatteriesData };
};

export const getDeviceChargeLevelTooltipAdapter = (t: TTypedTFunction, displayTimeZone?: string) => {
  return (_: any, target: am4charts.LineSeries) => {
    const tooltip = target.tooltipDataItem as any;
    const color = target?.dummyData?.colour;
    const date = formatDate(tooltip.dateX, displayTimeZone);
    const serialNumberField = target.dataFields.valueY;
    return `<div class="tooltip-container_device-charge-level"><span class="device-charge-level-24-hour-tooltip-title">${date}</span>
    </br><span class="line-chart-tooltip-container_device-charge-level"><div class="key" style="--colour: ${color}"></div>${t('CHARGE_LEVEL')}: {valueY}</span>
    </br><span class="line-chart-tooltip-container-tooltip-text serialnumber">${t('BATTERY_SN', { ns: 'performance' })}: ${serialNumberField}</span></div>`;
  };
};
